import { ActionTree, ActionContext } from 'vuex';
import moment from 'moment';

import { calendarClient } from '@/http-client';
import { camelToSnake, snakeToCamel, getUtcOffset } from '@/utils';
import {
  setEventIcon,
  removeEventIcon,
  rehydrateIcons,
  getEventIcon,
} from '@/utils/event-icon-cache';
import { saveUpcomingEvents, getUpcomingEvents } from '@/utils/events-db';

import { RootStateI } from '../state';
import {
  EventStateI,
  EventI,
  EditEventRequestI,
  GuestI,
  RSVPResponseI,
} from './state';

let latestListRangeKey = '';
let currentWs: WebSocket | null = null;
let lastWsPayloadStr = '';
let latestPayload: any = null;

async function persistLoadedEvents(
  context: ActionContext<EventStateI, RootStateI>,
): Promise<void> {
  const { events } = context.state;
  await saveUpcomingEvents(events);
  navigator.serviceWorker?.controller?.postMessage({ type: 'CHECK_EVENTS' });
}

export const actions: ActionTree<EventStateI, RootStateI> = {
  async getDetails(
    context: ActionContext<EventStateI, RootStateI>,
    eventId: string | number,
  ): Promise<void> {
    const { data } = await calendarClient.get(`/api/events/details/${eventId}`);
    const event = snakeToCamel(data) as EventI;
    // Rehydrate icon from local cache if the backend did not provide one.
    if (event && !event.icon) {
      event.icon = getEventIcon(event.id);
    }
    context.commit('setEvent', event);
  },

  async listcahedEvents(
    context: ActionContext<EventStateI, RootStateI>,
    payload: {
      startDate: number;
      endDate: number;
      calendarIds: number[] | string[];
      rangeKey: string;
    },
  ) {
    const rawEvents: EventI[] = await getUpcomingEvents();
    const events = rehydrateIcons(rawEvents) as EventI[];
    context.commit('setEvents', {
      events,
      rangeKey: payload.rangeKey,
      startDate: payload.startDate,
      endDate: payload.endDate,
    });
  },

  async listWs(
    context: ActionContext<EventStateI, RootStateI>,
    payload: {
      startDate: number;
      endDate: number;
      calendarIds: number[] | string[];
      rangeKey: string;
    },
  ): Promise<void> {
    const now = moment();
    const currentHour = now.hour();
    // check if hour is between 17 hrs and 8 hrs, next day, send an alert
    if (currentHour >= 17 || currentHour < 8) {
      console.log('You are viewing events outside of regular hours.');
      return Promise.resolve();
    }

    latestPayload = payload;
    const { rangeKey } = payload;
    latestListRangeKey = rangeKey;
    context.commit('setEventsLoading', true);

    const baseUrl = process.env.VUE_APP_URL_DG_CALENDAR || window.location.origin;
    const wsUrl = baseUrl.replace(/^http/, 'ws');
    const token = localStorage.getItem('token') || '';

    const calIdsParam = payload.calendarIds && payload.calendarIds.length > 0
      ? payload.calendarIds.join(',')
      : '';
    const url = `${wsUrl}/api/events/list/ws?token=${token}`;

    const onMessageHandler = async (event: MessageEvent) => {
      console.log('WebSocket message received:', event.data);
      try {
        const data = JSON.parse(event.data);

        let rawEvents: EventI[] = [];
        if (Array.isArray(data) && data.length === 0) {
          rawEvents = await getUpcomingEvents();
        } else {
          rawEvents = Array.isArray(data) ? snakeToCamel(data) as EventI[] : [];
        }

        const events = rehydrateIcons(rawEvents) as EventI[];
        context.commit('setEvents', {
          events,
          rangeKey,
          startDate: payload.startDate,
          endDate: payload.endDate,
        });
        context.commit('setEventsLoading', false);

        if (data !== null) {
          persistLoadedEvents(context).catch(() => undefined);
        }
      } catch (e) {
        console.error(e);
      }
    };

    if (currentWs && (currentWs.readyState === WebSocket.OPEN || currentWs.readyState === WebSocket.CONNECTING)) {
      currentWs.onmessage = onMessageHandler;

      if (currentWs.readyState === WebSocket.OPEN) {
        const payloadStr = JSON.stringify({
          start: payload.startDate,
          end: payload.endDate,
          calendarIds: payload.calendarIds,
        });
        if (payloadStr !== lastWsPayloadStr) {
          currentWs.send(payloadStr);
          lastWsPayloadStr = payloadStr;
        } else {
          context.commit('setEventsLoading', false);
        }
      } else {
        const prevOnOpen = currentWs.onopen;
        currentWs.onopen = function (event) {
          if (prevOnOpen) prevOnOpen.call(this, event);
          if (currentWs && currentWs.readyState === WebSocket.OPEN) {
            const payloadStr = JSON.stringify({
              start: payload.startDate,
              end: payload.endDate,
              calendarIds: payload.calendarIds,
            });
            if (payloadStr !== lastWsPayloadStr) {
              currentWs.send(payloadStr);
              lastWsPayloadStr = payloadStr;
            } else {
              context.commit('setEventsLoading', false);
            }
          }
        };
      }
      return Promise.resolve();
    }

    if (currentWs) {
      currentWs.close();
      currentWs = null;
    }

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      currentWs = ws;
      ws.onmessage = onMessageHandler;
      ws.onopen = () => {
        if (currentWs && currentWs.readyState === WebSocket.OPEN) {
          const payloadStr = JSON.stringify({
            start: payload.startDate,
            end: payload.endDate,
            calendarIds: payload.calendarIds,
          });
          currentWs.send(payloadStr);
          lastWsPayloadStr = payloadStr;
        }
      };

      ws.onerror = (err) => {
        console.error(err);
        if (rangeKey === latestListRangeKey) {
          context.commit('setEventsLoading', false);
        }
        reject(err);
      };

      ws.onclose = () => {
        if (rangeKey === latestListRangeKey) {
          context.commit('setEventsLoading', false);
        }
        resolve();
      };
    });
  },

  async list(
    context: ActionContext<EventStateI, RootStateI>,
    payload: {
      startDate: number;
      endDate: number;
      calendarIds: number[] | string[];
      rangeKey: string;
    },
  ): Promise<void> {
    const { rangeKey } = payload;
    latestListRangeKey = rangeKey;
    context.commit('setEventsLoading', true);

    try {
      const { data } = await calendarClient.get(
        `/api/events/listws?start=${payload.startDate}&end=${payload.endDate}&calendar_ids=${payload.calendarIds.join(',')}`,
      );

      if (rangeKey !== latestListRangeKey) {
        return undefined;
      }

      const rawEvents = Array.isArray(data) ? snakeToCamel(data) as EventI[] : [];
      const events = rehydrateIcons(rawEvents) as EventI[];
      context.commit('setEvents', {
        events,
        rangeKey,
        startDate: payload.startDate,
        endDate: payload.endDate,
      });
      context.commit('setEventsLoading', false);
      await persistLoadedEvents(context);
      return undefined;
    } catch (error) {
      if (rangeKey === latestListRangeKey) {
        context.commit('setEventsLoading', false);
      }
      throw error;
    }
  },

  clearList(
    context: ActionContext<EventStateI, RootStateI>,
    rangeKey = '',
  ): void {
    latestListRangeKey = rangeKey;
    context.commit('clearEvents', rangeKey);
  },

  async create(
    context: ActionContext<EventStateI, RootStateI>,
    payload: EventI,
  ): Promise<EventI> {
    const payloadWithUtc = { ...payload, utc: getUtcOffset() };
    const { data } = await calendarClient.post(
      '/api/events/add',
      camelToSnake(payloadWithUtc),
    );
    const returned = snakeToCamel(data) as EventI;
    // Preserve icon from payload; also persist to local cache
    const iconName = (payload as any).icon || '';
    if (iconName) {
      (returned as any).icon = iconName;
    }
    // Store against returned id (may differ from payload id on create)
    const storedId = (returned as any).id || (payload as any).id;
    if (iconName && storedId) {
      setEventIcon(storedId, iconName);
    }
    context.commit('addEvent', returned);
    return returned;
  },

  async delete(
    context: ActionContext<EventStateI, RootStateI>,
    payload: {
      eventId: string | number,
      type: 'this' | 'all' | 'following',
    },
  ): Promise<void> {
    const cleanId = String(payload.eventId).split('_')[0];
    const parsedId = /^\d+$/.test(cleanId) ? Number(cleanId) : cleanId;
    await calendarClient.get(`/api/events/delete/${parsedId}?type=${payload.type}`);
    removeEventIcon(payload.eventId);
    context.commit('deleteEvent', payload.eventId);
  },

  async addGuests(
    context: ActionContext<EventStateI, RootStateI>,
    payload: {
      eventId: string | number;
      emails: string[];
      userIds: number[];
    },
  ): Promise<GuestI[]> {
    const { data } = await calendarClient.post(
      `/api/events/${payload.eventId}/guests`,
      {
        emails: payload.emails,
        user_ids: payload.userIds,
      },
    );
    const response = snakeToCamel(data);
    let returned: GuestI[] = [];

    if (Array.isArray(response)) {
      returned = response;
    } else if (Array.isArray(response?.guests)) {
      returned = response.guests;
    } else if (Array.isArray(response?.data)) {
      returned = response.data;
    } else if (response?.guest) {
      returned = [response.guest];
    } else if (response?.id) {
      returned = [response];
    }

    if (returned.length > 0) {
      context.commit('addEventGuests', { eventId: payload.eventId, guests: returned });
    }
    return returned;
  },

  async removeGuest(
    context: ActionContext<EventStateI, RootStateI>,
    payload: {
      eventId: string | number;
      guestId: string;
    },
  ): Promise<void> {
    await calendarClient.delete(
      `/api/events/${payload.eventId}/guests/${payload.guestId}`,
    );
    context.commit('removeEventGuest', payload);
  },

  async update(
    context: ActionContext<EventStateI, RootStateI>,
    payload: EditEventRequestI,
  ): Promise<EventI> {
    const cleanId = String(payload.id).split('_')[0];
    const parsedId = /^\d+$/.test(cleanId) ? Number(cleanId) : cleanId;
    const payloadWithUtc = { ...payload, id: parsedId, utc: getUtcOffset() };
    const { data } = await calendarClient.post(
      '/api/events/edit',
      camelToSnake(payloadWithUtc),
    );
    const returned = snakeToCamel(data) as EventI;

    // Preserve icon from payload; also persist to local cache
    const iconName = (payload as any).icon || '';
    if (iconName) {
      (returned as any).icon = iconName;
    }
    // Store against returned id
    const storedId = (returned as any).id || (payload as any).id;
    if (iconName && storedId) {
      setEventIcon(storedId, iconName);
    }

    context.commit('updateEvent', returned);
    return returned;
  },

  async updateEvent(
    context: ActionContext<EventStateI, RootStateI>,
    payload: EventI,
  ): Promise<EventI> {
    const cleanId = String(payload.id).split('_')[0];
    const parsedId = /^\d+$/.test(cleanId) ? Number(cleanId) : cleanId;
    const payloadWithUtc = { ...payload, id: parsedId, utc: getUtcOffset() };
    const { data } = await calendarClient.post(
      '/api/events/update',
      camelToSnake(payloadWithUtc),
    );
    const returned = snakeToCamel(data) as EventI;

    // Preserve icon from payload; also persist to local cache
    const iconName = (payload as any).icon || '';
    if (iconName) {
      (returned as any).icon = iconName;
    }
    // Store against returned id
    const storedId = (returned as any).id || (payload as any).id;
    if (iconName && storedId) {
      setEventIcon(storedId, iconName);
    }

    context.commit('updateEvent', returned);
    return returned;
  },

  async submitRSVP(
    context: ActionContext<EventStateI, RootStateI>,
    payload: RSVPResponseI,
  ): Promise<void> {
    const response = await calendarClient.get('/api/rsvp', {
      params: camelToSnake(payload),
    });

    const rsvpData = snakeToCamel(response.data);
    context.commit('setRSVPResponse', rsvpData);
  },

};

// Ensure we only add the listener once (e.g. across HMR reloads)
if (typeof window !== 'undefined' && !(window as any).visibilityListenerAdded) {
  (window as any).visibilityListenerAdded = true;

  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  document.addEventListener('visibilitychange', () => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (document.visibilityState === 'hidden') {
      if (currentWs) {
        currentWs.close();
        currentWs = null;
      }
    } else if (document.visibilityState === 'visible') {
      reconnectTimeout = setTimeout(() => {
        if (!currentWs && latestPayload) {
          // Dispatch a custom event that can be listened to in App.vue or store/index.ts
          // avoiding circular dependency from importing store directly.
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('reconnect-ws', { detail: latestPayload }));
          }
        }
      }, 500); // 500ms debounce/cooldown
    }
  });
}
